// LocalPerLocationFrame — per-physical-location Google Business Profile
// blocks: one card per location with an impressions donut broken down by
// whatever device x surface dimensions that location's data actually
// carries (spec screenshot: Brook Hollow/Rigsby/Shaenfield show 4 slices —
// Mobile Search/Mobile Maps/Desktop Search/Desktop Maps — Marbach shows
// only 2 — Mobile/Desktop; never assume a fixed slice count), plus a
// reviews tile. Recovered + reworked from the LocalPerLocationFrame deleted
// in 44ce9c1e7 (which the account has no per-location dimension for) now
// that per-location GBP retrieval has been confirmed retrievable — this is
// a new build against the real per-location contract (name + impressions +
// by_surface + reviews), not a revival of the old calls/clicks/directions
// bar-chart shape, which doesn't match what the backend actually carries
// per location.
//
// Self-contained: only recharts + named theme tokens (no hardcoded hex),
// same donut+legend construction as CallOutcomesDonut.tsx and the same
// dark "tile IS the chart" KPI convention as HeroKpiTile in primitives.tsx
// for the reviews readout.

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ChangePctBadge } from "@/components/client-analytics/primitives";

export interface LabeledCount {
  label: string;
  value: number;
}

export interface LocationLocalMetrics {
  // Stable Google resource-path identifier (e.g.
  // "accounts/.../locations/..."), NOT the display name -- React key and
  // any future lookup use this, never `name`. Two GBP locations can share a
  // display name (chain with two sites in the same district, or a name AA
  // truncated); keying on name would silently collapse them into one card.
  // Documented recurring failure class in this codebase (duplicate row
  // labels are real data -- see architect-briefs/BRIEF-pair-watchers-
  // 20260809.md item 10, same class of bug as the ad-creative/keyword
  // dedup-by-label incidents).
  location_id: string;
  name: string;
  impressions: number;
  // Device x surface impression breakdown for THIS location -- granularity
  // varies per location (2 slices for some, 4 for others per the spec
  // screenshot), so this renders whatever slices are present rather than
  // assuming a fixed set.
  by_surface: LabeledCount[];
  impressions_change_pct?: number | null;
  reviews?: number | null;
  reviews_change_pct?: number | null;
  // Weighted average rating for THIS location in-window -- null when
  // reviews is 0/null (can't compute a rating with no reviews), rendered
  // as a quiet inline number next to the review count, never as 0.0 (which
  // would read as a terrible rating rather than "no data"). Deliberately
  // more granular than the account-wide "All Locations · Average Rating"
  // tile: within one multi-location account the per-location spread is
  // real signal an aggregate hides (e.g. one struggling site dragging a
  // fine-looking account average) -- Carl's ruling 2026-08-09.
  average_rating?: number | null;
}

export interface LocalPerLocationFrameProps {
  locations: LocationLocalMetrics[];
}

// Sample data for standalone preview / storybook-style rendering. Mirrors
// the Alamo City Urgent Care reference screenshot (2026-08-09) -- note
// Marbach's 2-slice breakdown vs. the other three locations' 4-slice one,
// the exact "different granularity per location" case this frame has to
// handle without assuming a fixed slice count.
export const demoLocations: LocationLocalMetrics[] = [
  {
    location_id: "accounts/110427111454670243689/locations/2335572523169329060",
    name: "Brook Hollow – North San Antonio",
    impressions: 4897,
    impressions_change_pct: 21,
    reviews: 4,
    reviews_change_pct: -20,
    average_rating: 5.0,
    by_surface: [
      { label: "Mobile Search", value: 2458 },
      { label: "Mobile Maps", value: 1210 },
      { label: "Desktop Search", value: 938 },
      { label: "Desktop Maps", value: 291 },
    ],
  },
  {
    location_id: "accounts/110427111454670243689/locations/8814092233614887421",
    name: "Marbach",
    impressions: 6565,
    impressions_change_pct: 17,
    reviews: 2,
    reviews_change_pct: 100,
    average_rating: 4.64,
    by_surface: [
      { label: "Mobile", value: 5522 },
      { label: "Desktop", value: 1043 },
    ],
  },
  {
    location_id: "accounts/110427111454670243689/locations/5602948870319114703",
    name: "Rigsby",
    impressions: 3427,
    impressions_change_pct: 26,
    reviews: 3,
    reviews_change_pct: 0,
    average_rating: 5.0,
    by_surface: [
      { label: "Search", value: 2250 },
      { label: "Maps", value: 1177 },
    ],
  },
  {
    location_id: "accounts/110427111454670243689/locations/9147638502271056384",
    name: "Shaenfield",
    impressions: 4178,
    impressions_change_pct: 7.57,
    reviews: 3,
    reviews_change_pct: -25,
    average_rating: 4.33,
    by_surface: [
      { label: "Search", value: 2584 },
      { label: "Maps", value: 1594 },
    ],
  },
];

// Cycled by index rather than a per-label lookup -- the label set itself
// varies per location (see doc comment above), so there's no fixed
// "Mobile Search is always blue" identity to preserve across cards, unlike
// CallOutcomesDonut's fixed call-outcome palette.
//
// Previously [coral, ring, teal, warm-border-strong] -- confirmed live low
// contrast on both Alamo City and AAA Fence's 4-slice cards (Mobile
// Search/Mobile Maps vs. Desktop Search/Desktop Maps): coral+ring are both
// blue (deep azure + bright accent blue) and teal+warm-border-strong are
// both grey, so a 4-category donut effectively only had TWO distinguishable
// colors, muddy at card size even though the two pairs are individually
// legible zoomed in. Swapped to the dashboard's own multi-series chart
// palette (TrendChart's SERIES_COLORS, index.css `--color-chart-series-*`)
// -- 4 genuinely different hues (azure/violet/mauve/indigo) built exactly
// for "distinguish N categories at a glance," not a lightness variant of
// the same 2 hues. Still named theme tokens, no raw hex.
const SLICE_COLORS = [
  "var(--color-chart-series-1)",
  "var(--color-chart-series-2)",
  "var(--color-chart-series-3)",
  "var(--color-chart-series-4)",
];

const DONUT_SIZE = 150;

function LocationDonut({ by_surface }: { by_surface: LabeledCount[] }) {
  const slices = by_surface.filter((s) => s.value > 0);
  const total = slices.reduce((sum, s) => sum + s.value, 0);

  if (total <= 0) {
    return (
      <div
        className="flex items-center justify-center text-dash-context text-muted-foreground"
        style={{ height: DONUT_SIZE }}
      >
        No impression data yet
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative shrink-0" style={{ height: DONUT_SIZE, width: DONUT_SIZE }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={44}
              outerRadius={68}
              paddingAngle={2}
              stroke="var(--color-card)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((s, i) => (
                <Cell key={s.label} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid var(--color-border)",
                fontSize: 12,
              }}
              formatter={(value: number, name) => [
                `${value.toLocaleString()} (${total > 0 ? Math.round((value / total) * 100) : 0}%)`,
                name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="tabular text-dash-value font-bold text-foreground">{total.toLocaleString()}</span>
          <span className="text-dash-context text-muted-foreground">Impressions</span>
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-1.5">
        {slices.map((s, i) => (
          <li key={s.label} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }}
            />
            {/* No `truncate` -- see CallOutcomesDonut.tsx's identical legend
                for why: `overflow: hidden` disables the flex item's
                automatic min-content protection, letting the shrink-0 count
                column squeeze this label to 2-3 characters. Unset
                width/overflow lets it wrap instead, so the full surface
                label is always visible without relying on the `title`
                tooltip. */}
            <span className="text-dash-context text-foreground" title={s.label}>
              {s.label}
            </span>
            <span className="tabular ml-auto shrink-0 text-dash-context text-muted-foreground">
              {s.value.toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LocationCard({ location }: { location: LocationLocalMetrics }) {
  const hasReviews = location.reviews !== null && location.reviews !== undefined;
  // null when reviews is 0/null (can't compute a rating with no reviews) --
  // never render 0.0, which would read as a terrible rating rather than
  // "no data".
  const hasRating = location.average_rating !== null && location.average_rating !== undefined;
  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-dash-context font-semibold text-foreground" title={location.name}>
          {location.name}
        </span>
        <ChangePctBadge pct={location.impressions_change_pct} />
      </div>
      <LocationDonut by_surface={location.by_surface} />
      {hasReviews && (
        <div className="rounded-md bg-foreground text-background px-3 py-2 flex items-center justify-between gap-2">
          <span className="text-dash-label font-semibold uppercase tracking-wide opacity-70">Reviews</span>
          <div className="flex items-center gap-2">
            {hasRating && (
              // Quiet inline number next to the review count, not a new
              // card/chart -- per-location rating spread is the actual
              // insight (a struggling site can hide behind a fine
              // account-wide average), but stays visually secondary to
              // the review count itself.
              <span className="tabular text-dash-context opacity-70">{location.average_rating!.toFixed(1)}★</span>
            )}
            <span className="tabular text-dash-value font-bold">{formatNum(location.reviews)}</span>
            <ChangePctBadge pct={location.reviews_change_pct} />
          </div>
        </div>
      )}
    </div>
  );
}

function formatNum(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return n.toLocaleString();
}

export function LocalPerLocationFrame({ locations }: LocalPerLocationFrameProps) {
  if (!locations || locations.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {locations.map((loc) => (
        <LocationCard key={loc.location_id} location={loc} />
      ))}
    </div>
  );
}
